import { FC, useCallback, useEffect, useState } from 'react';

import { BaseScreen } from '../../components/BaseScreen.tsx';
import ErrorMessage from '../../components/ErrorMessage.tsx';
import { Link } from '../../components/Link.tsx';
import Password from '../../components/Password.tsx';
import { useMergedConfig } from '../../context/MergedConfigProvider.tsx';
import { useMParticle } from '../../hooks/useMparticle.ts';
import { useTranslations } from '../../hooks/useTranslations.ts';
import {
  API_ERROR_CODES,
  apiLogin,
  apiResetPasswordRequest,
  ApiResetPasswordRequestResult,
  apiSetPassword,
  IdentityStatus,
} from '../../onboarding-api';
import { translationKeys } from '../../translations/translationKeys.ts';
import { getTranslatedApiError } from '../../utils/getTranslatedApiError.ts';
import { SignUpState } from '../SignUpJourney.tsx';

type Props = {
  signUpState: SignUpState;
  onGoBack: () => void;
  onForgotPassword: (requiresOtp: boolean) => void;
  onCompleted: (password: string, requiresOtp: boolean, oneTimeToken?: string) => void;
};

export const PasswordScreen: FC<Props> = ({ signUpState, onGoBack, onForgotPassword, onCompleted }) => {
  const [password, setPassword] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLocked, setIsLocked] = useState(false); // happens when too many failed login attempts
  const [error, setError] = useState<string | null>(null);
  const [passwordIsValid, setPasswordIsValid] = useState<boolean>(false);
  const [validationError, setValidationError] = useState<string | undefined>(undefined);
  const { journeyId, lang: language, region } = useMergedConfig();
  const { translate } = useTranslations();

  const { logRegistrationPassword, logRegistrationForgotPassword, setUserData } = useMParticle();

  const isDisabled = !passwordIsValid || isSubmitting;

  const isNewPassword = signUpState.emailStatus === IdentityStatus.new || signUpState.requiresSetPassword;

  let formTitle = translate(translationKeys.PASSWORD_SCREEN_TITLE_NEW);
  let placeholder = translate(translationKeys.PASSWORD_SCREEN_TITLE_NEW);
  let showInstructions = true;
  let disableValidation = false;
  let showForgotPassword = false;
  if (!isNewPassword) {
    formTitle = translate(translationKeys.PASSWORD_SCREEN_TITLE_EXISTING);
    placeholder = translate(translationKeys.PASSWORD_SCREEN_PLACEHOLDER_EXISTING);
    showInstructions = false;
    disableValidation = true;
    showForgotPassword = true;
  }

  useEffect(() => {
    if (validationError && passwordIsValid) {
      setValidationError(undefined);
    }
  }, [passwordIsValid, validationError]);

  const onBlur = useCallback(() => {
    if (!passwordIsValid) {
      setValidationError(translate(translationKeys.PASSWORD_SCREEN_VALIDATION_MESSAGE));
    } else {
      setValidationError(undefined);
    }
  }, [passwordIsValid, translate]);

  const onSubmit = useCallback(async () => {
    if (!isDisabled) {
      setError(null);
      setIsSubmitting(true);

      if (isNewPassword) {
        logRegistrationPassword();
      }

      const apiCallFunc = isNewPassword ? apiSetPassword : apiLogin;
      const { data, errorCode, errorMessage } = await apiCallFunc(
        password,
        signUpState.csrfToken,
        journeyId,
        language,
        region
      );
      if (data) {
        if (data.p_id) {
          setUserData({ personId: data.p_id });
        }

        onCompleted(password, data.requiresOtp || false, data.oneTimeToken);
      } else if (errorCode) {
        setError(getTranslatedApiError(errorCode, translate, errorMessage));
        if (errorCode === API_ERROR_CODES.LOGIN_TOO_MANY_ATTEMPTS) {
          setIsLocked(true);
        }
      }

      setIsSubmitting(false);
    }
  }, [
    isNewPassword,
    isDisabled,
    onCompleted,
    password,
    signUpState.csrfToken,
    journeyId,
    language,
    region,
    logRegistrationPassword,
    setUserData,
    translate,
  ]);

  const onForgotPasswordLinkClicked = useCallback(async () => {
    if (!isSubmitting) {
      setError(null);
      setIsSubmitting(true);

      const { data, errorCode, errorMessage }: ApiResetPasswordRequestResult = await apiResetPasswordRequest(
        signUpState.csrfToken,
        journeyId,
        language,
        region
      );
      if (data) {
        onForgotPassword(data.requiresOtp);
        logRegistrationForgotPassword();
      } else if (errorCode) {
        setError(getTranslatedApiError(errorCode, translate, errorMessage));
      }

      setIsSubmitting(false);
    }
  }, [
    isSubmitting,
    logRegistrationForgotPassword,
    onForgotPassword,
    signUpState.csrfToken,
    journeyId,
    language,
    region,
    translate,
  ]);

  return (
    <BaseScreen
      headerBackBtnVisible={true}
      headerBackBtnOnClick={onGoBack}
      headerBackBtnDisabled={isSubmitting}
      formTitle={formTitle}
      footerBtnLabel={translate(translationKeys.FOOTER_BUTTON_LABEL)}
      footerBtnOnClick={onSubmit}
      footerBtnLoading={isSubmitting}
      footerBtnDisabled={!passwordIsValid || isLocked}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit();
        }}
      >
        <Password
          updateFieldValue={setPassword}
          updateFieldIsValid={(valid) => {
            setPasswordIsValid(valid && password.length > 0);
          }}
          triggerFieldChange={() => {}}
          onBlur={onBlur}
          label={translate(translationKeys.PASSWORD_INPUT_LABEL)}
          placeholder={placeholder}
          fullWidth={true}
          showInstructions={showInstructions}
          disableValidation={disableValidation}
          email={signUpState.email}
          disabled={isSubmitting || isLocked}
          validatemessage={validationError}
        />
      </form>
      {error && <ErrorMessage message={error} />}
      {showForgotPassword && (
        <div className={'flex justify-center'}>
          <p className={'text-sm'}>
            <Link
              label={translate(translationKeys.PASSWORD_SCREEN_FORGOT_PASSWORD_LABEL)}
              // Use onMouseDown here so that event fires before the Password onBlur
              onMouseDown={onForgotPasswordLinkClicked}
              disabled={isSubmitting}
            ></Link>
          </p>
        </div>
      )}
    </BaseScreen>
  );
};
